import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useDocumentDownloadActions } from 'features/knowledgeDocumentViewer/hooks/useDocumentDownloadActions'
import {
  resolveDerivedOriginalFileRef,
  resolveDerivedPdfFileRef,
} from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import {
  getDownloadDocumentKind,
  isOfficeDownloadKind,
} from 'features/knowledgeDocumentViewer/utils/downloadDocumentKind'
import { isCmmsRecordId } from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { WorkOrderDownloadButton } from 'features/knowledgeDocumentViewer/workOrders/WorkOrderDownloadButton'
import { useRolodexSignedURLs } from 'features/rolodex'
import { Download } from 'lucide-react'
import type { ComponentProps, MouseEvent } from 'react'
import { useMemo } from 'react'

interface DownloadSearchResultButtonProps {
  document: DocumentTableRow
  pageNumber?: number
  size?: ComponentProps<typeof Button>['size']
}

export function DownloadSearchResultButton(
  props: DownloadSearchResultButtonProps
) {
  return isCmmsRecordId(props.document.id) ? (
    <WorkOrderDownloadButton
      documentId={props.document.id}
      pageNumber={props.pageNumber}
      size={props.size}
    />
  ) : (
    <SourceDocumentDownloadButton {...props} />
  )
}

function SourceDocumentDownloadButton({
  document,
  pageNumber,
  size = 'icon-lg',
}: DownloadSearchResultButtonProps) {
  const { t } = useLingui()
  const { siteID } = useKnowledgeDocumentDataContext()

  const downloadKind = getDownloadDocumentKind(undefined, document.filename)
  const isOffice = isOfficeDownloadKind(downloadKind)

  const pdfUrlParams = useMemo(
    () =>
      resolveDerivedPdfFileRef({
        siteId: siteID,
        bqDocumentId: document.bqDocumentId ?? document.id,
        filename: document.filename,
        cantileverDocumentEntryId: document.cantileverDocumentEntryId,
      }),
    [
      document.bqDocumentId,
      document.cantileverDocumentEntryId,
      document.filename,
      document.id,
      siteID,
    ]
  )
  const originalUrlParams = useMemo(
    () =>
      resolveDerivedOriginalFileRef({
        siteId: siteID,
        bqDocumentId: document.bqDocumentId ?? document.id,
        filename: document.filename,
        cantileverDocumentEntryId: document.cantileverDocumentEntryId,
      }),
    [
      document.bqDocumentId,
      document.cantileverDocumentEntryId,
      document.filename,
      document.id,
      siteID,
    ]
  )

  const { url: pdfUrl, isLoading: pdfUrlLoading } =
    useRolodexSignedURLs(pdfUrlParams)
  const { url: originalUrl, isLoading: originalUrlLoading } =
    useRolodexSignedURLs(isOffice ? originalUrlParams : undefined)

  const {
    isDownloading,
    handleFullPdfDownload,
    handleOriginalDownload,
    handleCurrentPageDownload,
  } = useDocumentDownloadActions({
    pdfSignedUrl: pdfUrl,
    originalSignedUrl: originalUrl,
    documentFilename: document.filename,
    currentPageNumber: pageNumber ?? 1,
  })

  const usesPdfOnlyControl = !isOffice || Boolean(pageNumber)
  const isDisabled = usesPdfOnlyControl
    ? !pdfUrl || !document.filename || pdfUrlLoading
    : (!pdfUrl && !originalUrl) ||
      !document.filename ||
      pdfUrlLoading ||
      originalUrlLoading

  const stopRowNavigation = (event?: MouseEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
  }

  const documentLabel = document.documentTitle || document.title
  const downloadAriaLabel = t`Download ${documentLabel}`

  if (usesPdfOnlyControl) {
    return (
      <Button
        type='button'
        variant='inputRaised'
        size={size}
        title={
          pageNumber ? t`Download Page ${pageNumber}` : t`Download Document`
        }
        aria-label={downloadAriaLabel}
        onClick={(event) => {
          stopRowNavigation(event)
          void (pageNumber
            ? handleCurrentPageDownload()
            : handleFullPdfDownload())
        }}
        disabled={isDisabled || isDownloading}>
        <Download size={18} strokeWidth={1.5} />
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='inputRaised'
          size={size}
          title={t`Download Document`}
          aria-label={downloadAriaLabel}
          disabled={isDisabled || isDownloading}
          onClick={stopRowNavigation}>
          <Download size={18} strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        onClick={(event) => {
          event.stopPropagation()
        }}>
        <DropdownMenuItem
          disabled={!originalUrl}
          onSelect={(event) => {
            event.preventDefault()
            void handleOriginalDownload()
          }}>
          <Trans>Download original</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!pdfUrl}
          onSelect={(event) => {
            event.preventDefault()
            void handleFullPdfDownload()
          }}>
          <Trans>Download PDF</Trans>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
